import { useCallback, useEffect, useState } from "react";
import { MCPClient } from "@/services/mcpClient";
import { saveTools, saveResources } from "@/services/db";
import type { MCPContextSnapshot, MCPServer } from "@/types";

const client = new MCPClient();

export const useMcpContext = (server?: MCPServer) => {
  const [snapshot, setSnapshot] = useState<MCPContextSnapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefreshed, setAutoRefreshed] = useState(false);

  /**
   * Loads context snapshot from cache (fast, no server calls)
   */
  const loadFromCache = useCallback(async () => {
    if (!server) return;
    setLoading(true);
    setError(null);
    try {
      const next = await client.getContextSnapshot(server);
      setSnapshot(next);
    } catch (err: any) {
      setError(err?.message ?? "Unable to load MCP context from cache.");
    } finally {
      setLoading(false);
    }
  }, [server]);

  /**
   * Refreshes context snapshot from server (slower, updates cache)
   */
  const refresh = useCallback(async () => {
    if (!server) return;
    setRefreshing(true);
    setError(null);
    try {
      const next = await client.refreshContextSnapshot(server);
      setSnapshot(next);
    } catch (err: any) {
      setError(err?.message ?? "Unable to fetch MCP context.");
    } finally {
      setRefreshing(false);
    }
  }, [server]);

  useEffect(() => {
    if (!server) {
      setSnapshot(null);
      setAutoRefreshed(false);
      return;
    }
    // On initial load, use cached data for better performance
    setAutoRefreshed(false);
    loadFromCache();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [server]);

  // Auto-refresh if we have no cached data (first-time connection)
  useEffect(() => {
    if (snapshot && server && !loading && !refreshing && !autoRefreshed) {
      const hasData = (snapshot.tools?.length ?? 0) > 0 || (snapshot.resources?.length ?? 0) > 0;
      if (!hasData && !snapshot.error) {
        // No cached data and no error - likely first connection, refresh from server
        setAutoRefreshed(true);
        refresh();
      }
    }
  }, [snapshot, server, loading, refreshing, refresh, autoRefreshed]);

  return { snapshot, loading, refreshing, error, refresh, loadFromCache };
};
